import { z } from 'zod'

const dateOrString = z.union([z.date(), z.string()])

// ─── Student Dashboard ───────────────────────────────────

export const DashboardStatsSchema = z.object({
  completedCourses: z.number(),
  inProgressCourses: z.number(),
  totalEnrollments: z.number(),
})

export const CourseProgressItemSchema = z.object({
  total: z.number(),
  completed: z.number(),
  percentage: z.number(),
})

export const InProgressCourseSchema = z.object({
  enrollment: z.object({
    id: z.string(),
    userId: z.string(),
    courseId: z.string(),
    enrolledAt: dateOrString,
    status: z.string(),
    tenantId: z.string().nullable(),
  }),
  course: z
    .object({
      id: z.string(),
      title: z.string(),
    })
    .passthrough(),
  progress: CourseProgressItemSchema,
  currentLesson: z.any().nullable(),
})

export const CompletedCourseSchema = z.object({
  enrollment: z.object({
    id: z.string(),
    userId: z.string(),
    courseId: z.string(),
    enrolledAt: dateOrString,
    status: z.string(),
    tenantId: z.string().nullable(),
  }),
  course: z
    .object({
      id: z.string(),
      title: z.string(),
    })
    .passthrough(),
  progress: CourseProgressItemSchema,
})

export const RecentCompletionSchema = z
  .object({
    id: z.string(),
    lessonId: z.string(),
    completedAt: dateOrString,
  })
  .passthrough()

export const StudentDashboardSchema = z.object({
  inProgress: z.array(InProgressCourseSchema),
  completed: z.array(CompletedCourseSchema),
  recentCompletions: z.array(RecentCompletionSchema),
  stats: DashboardStatsSchema,
})

export type StudentDashboard = z.infer<typeof StudentDashboardSchema>
export type DashboardStats = z.infer<typeof DashboardStatsSchema>

// ─── Teacher Dashboard ───────────────────────────────────

export const TeacherDashboardSchema = z.object({
  courseCount: z.number(),
  lessonCount: z.number(),
  studentCount: z.number(),
  completionRate: z.number(),
  recentCourses: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      createdAt: dateOrString,
    }),
  ),
})

export type TeacherDashboard = z.infer<typeof TeacherDashboardSchema>

// ─── Certificates ────────────────────────────────────────

export const CertificateSchema = z.object({
  courseId: z.string(),
  courseName: z.string(),
  issuedDate: z.string(),
})

export type Certificate = z.infer<typeof CertificateSchema>

// ─── Skill Analytics ─────────────────────────────────────

export const SkillLevelSchema = z.object({
  subject: z.string(),
  level: z.number().min(0).max(100),
  fullMark: z.number(),
})

export const SkillProgressPointSchema = z
  .object({
    name: z.string(),
  })
  .catchall(z.union([z.string(), z.number()]))

export const RecommendedCourseSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  level: z.string(),
})

export const SkillAnalyticsSchema = z.object({
  skills: z.array(SkillLevelSchema),
  progressHistory: z.array(SkillProgressPointSchema),
  skillNames: z.array(z.string()),
  weakestSkill: z.string().nullable(),
  recommendedCourses: z.array(RecommendedCourseSchema),
})

export type SkillLevel = z.infer<typeof SkillLevelSchema>
export type SkillProgressPoint = z.infer<typeof SkillProgressPointSchema>
export type RecommendedCourse = z.infer<typeof RecommendedCourseSchema>
export type SkillAnalytics = z.infer<typeof SkillAnalyticsSchema>
